import { FC } from 'react';
import toast from 'react-hot-toast';
import { useNavigate, useSearchParams } from 'react-router-dom';

import {
  IconButton,
  RyeconClose,
  RyeconCode,
  RyeconCompass,
  RyeconShare,
  Sizes,
} from '@dbt-labs/sourdough';

import { ResourceChip } from '@/components/ResourceChip';
import { useDbtCloudProject } from '@/context/DbtCloudProjectProvider';
import { useAllowDbtCloudLinks, useDbtCloudAccount } from '@/context/DbtCloudProvider';
import { navigateNewTab, useLinkToExplorer } from '@/hooks/links';
import { useTargetEnvironmentType } from '@/hooks/useTargetEnvironmentType';

interface ProjectPanelHeaderProps {
  projectId: number | null;
  environmentId: number | null;
  cloudProjectName: string | null;
  coreProjectName: string | null;
  hasProjectAccess: boolean;
}

export const ProjectPanelHeader: FC<ProjectPanelHeaderProps> = ({
  projectId,
  environmentId,
  cloudProjectName,
  coreProjectName,
  hasProjectAccess,
}) => {
  const { accountId } = useDbtCloudAccount();
  const navigate = useNavigate();
  const enableExternalLinks = useAllowDbtCloudLinks();
  const { projectId: originProjectId } = useDbtCloudProject();
  const getTargetEnvironmentType = useTargetEnvironmentType();
  const [, setSearchParams] = useSearchParams();
  const projectOverviewLink = useLinkToExplorer('project');

  function handlePanelClose() {
    setSearchParams((params) => {
      params.delete('projectId');
      params.delete('environmentId');
      return params;
    });
  }
  if (!accountId || !projectId) return null;

  const productName = 'Catalog';
  return (
    <>
      <div className="Fborder-b mb-4 flex w-full items-center border-borderMuted pb-4">
        <span className="flex-1">
          <ResourceChip resourceType="project" />
        </span>
        <span className="flex-0 flex items-center gap-2">
          <IconButton
            size={Sizes.lg}
            ryecon={RyeconCompass}
            className="size-6 align-middle"
            tooltip={
              hasProjectAccess
                ? `View in ${productName}`
                : `You don't have permission to view project ${productName}`
            }
            isDisabled={!hasProjectAccess}
            onClick={() => {
              const navigateTo = projectOverviewLink({
                accountId,
                projectId,
                environmentType: getTargetEnvironmentType(environmentId || 0),
                originProject: originProjectId,
              });
              navigateNewTab(navigateTo, navigate, enableExternalLinks);
            }}
          />

          <IconButton
            size={Sizes.lg}
            ryecon={RyeconCode}
            className="size-6 align-middle"
            isDisabled={true}
          />

          <IconButton
            size={Sizes.lg}
            ryecon={RyeconShare}
            label="Copy Link to Page"
            tooltip="Copy Link to Page"
            className="size-6 align-middle"
            onClick={() => {
              navigator.clipboard.writeText(window.location.href);
              toast.success('Copied link to clipboard', { id: 'copy-link' });
            }}
          />

          <IconButton
            size={Sizes.lg}
            ryecon={RyeconClose}
            className="size-6 align-middle"
            label="Close Panel"
            tooltip="Close Panel"
            testId="close-project-panel-button"
            onClick={handlePanelClose}
          />
        </span>
      </div>
      <div>
        <div className="text-xl font-medium">{cloudProjectName || coreProjectName}</div>
        <div className="mb-4 mt-1 text-xs font-normal">{coreProjectName}</div>
      </div>
    </>
  );
};
